import {
    getFormattedPhoneNumber,
    getNationalPhoneNumberForm,
} from '@liveauctioneers/caterwaul-components/lib/Forms/PhoneForm/utils/phoneForm';
import { AddressDisplay } from '@liveauctioneers/hammer-ui-core/addressDisplay';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { SubtleLink } from '@liveauctioneers/hammer-ui-core/link';
import { Radio } from '@liveauctioneers/hammer-ui-core/radio';
import { LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getUpdateAccountLoading } from '@/redux/modules/account/updateAccount/updateAccount';
import { updateBidderAddress } from '@/redux/modules/account/updateAccount/updateAccount.actions';
import { isUserDataLoading } from '@/redux/modules/account/user/user.selectors';
import { openAddressModal } from '@/redux/modules/modal';
import { BidderAddress } from '@/types/BidderAddresses';
type Props = {
    address: BidderAddress;
    canEdit: boolean;
    onSelect: (address: BidderAddress) => void;
    selected: boolean;
    selectedId: number;
    showRemoveButton: boolean;
    showSetDefaultButton?: boolean;
};

const BidderAddressListItem = ({
    address,
    canEdit,
    onSelect,
    selected,
    showRemoveButton,
    showSetDefaultButton,
}: Props) => {
    const dispatch = useAppDispatch();

    const updateAccountLoading = useAppSelector(getUpdateAccountLoading);
    const userDataLoading = useAppSelector(isUserDataLoading);

    const openEditModal = (address: BidderAddress, id: number) => {
        dispatch(openAddressModal({ address, addressID: id }));
    };
    const removeAddress = (address: BidderAddress) => {
        dispatch(updateBidderAddress(address.bidderId, { ...address, enabled: false }, ''));
    };

    const formattedPhoneNumber = getNationalPhoneNumberForm(address.phoneNumber);
    const phoneNumber =
        formattedPhoneNumber.countryCode === 'US' || formattedPhoneNumber.countryCode === 'CA'
            ? getFormattedPhoneNumber(address.phoneNumber)
            : formattedPhoneNumber.phoneNumber;

    const addressComponent = (
        <AddressRow>
            <AddressCol>
                {address.isDefault && (
                    <LabelMini className="mb-2xs inline-block">
                        <FormattedMessage id="default" />
                    </LabelMini>
                )}
                <AddressDisplay
                    address={address}
                    className="mb-0"
                    testid="address"
                />
            </AddressCol>
        </AddressRow>
    );

    return (
        <StyledBidderAddessListItem data-testid="addressListItem">
            {showSetDefaultButton ? (
                <AddressRow>
                    <AddressCol>
                        {address.isDefault && (
                            <LabelMini className="mb-2xs inline-block">
                                <FormattedMessage id="default" />
                            </LabelMini>
                        )}
                        <AddressDisplay
                            address={{ ...address, phoneNumber }}
                            className="mb-0"
                            testid="address"
                        />
                    </AddressCol>
                </AddressRow>
            ) : (
                <Radio.Option
                    checked={selected}
                    className="items-start pl-[26px] [&_input]:mt-[3px]"
                    disabled={updateAccountLoading || userDataLoading}
                    name="bidderAddress"
                    onChange={() => {
                        if (!selected) {
                            onSelect(address);
                        }
                    }}
                    value={String(address.addressId)}
                >
                    {addressComponent}
                </Radio.Option>
            )}
            {canEdit ? (
                <StyledEditRow $radioIndentation={!showSetDefaultButton}>
                    <SubtleLink
                        className="underline"
                        onClick={() => openEditModal(address, address.addressId)}
                    >
                        <FormattedMessage id="addresses.edit" />
                    </SubtleLink>
                    {showRemoveButton && !address.isDefault && (
                        <SubtleLink
                            className="ml-sm underline"
                            onClick={() => removeAddress(address)}
                        >
                            <FormattedMessage id="addresses.remove" />
                        </SubtleLink>
                    )}
                    {!address.isDefault && showSetDefaultButton && (
                        <SubtleLink
                            className="ml-sm underline"
                            onClick={() => onSelect(address)}
                        >
                            <FormattedMessage id="addresses.setDefault" />
                        </SubtleLink>
                    )}
                </StyledEditRow>
            ) : (
                <span></span>
            )}
        </StyledBidderAddessListItem>
    );
};

export default BidderAddressListItem;

const StyledBidderAddessListItem = styled.div`
    margin-bottom: 24px;
`;

const StyledEditRow = styled.div<{ $radioIndentation: boolean }>`
    margin-top: 4px;
    margin-left: ${({ $radioIndentation }) => ($radioIndentation ? 32 : 0)}px;
`;

const AddressRow = styled.div`
    display: flex;
`;

const AddressCol = styled.div`
    display: flex;
    flex-direction: column;
`;
